//******************************************************************//
// Subir carpeta tributaria personalizada y créditos para enrolados //
// Api:        `${apiUrl}/subircarpeta/uploadcarpeta`               //
// Parametros:                                                      //
//             file:   archivo pdf                                  //
//             RutCli: rut cliente                                  //
//             RutEnr: rut contribuyente enrolado                   //
//             NomCli: nombre contribuyente                         //             
//             TipCli: 1=personalizada 2=créditos                   //
//******************************************************************//
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { TextField, Button, Box, Tooltip, MenuItem } from '@mui/material';

import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import { ThemeProvider, useTheme } from '@mui/material/styles';

import { CustomToolbar } from "../../components/datagrid/CustomToolbar"; // DataGrid
import { getCustomTheme } from "../../theme/customTheme";                // Tema

import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.css';
import { jwtDecode } from 'jwt-decode';
import { RiFolderCheckFill, RiUpload2Line, RiReplyAllFill } from "react-icons/ri";

const Subircarpetaenr = ({ setTitle, isSidebarOpen, modo }) => {
  const apiUrl = process.env.REACT_APP_API_URL;

  // Uso del Tema //
  const themeBase = useTheme();
  const customTheme = getCustomTheme(themeBase, modo);

  const inputRef = useRef(null);   // 👉 referencia al input file

  // Estados //
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [rut, setRut] = useState('');
  const [nomUsuario, setNomUsuario] = useState(''); // Estado para almacenar el nombre del usuario //
  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null); // 👈 archivo PDF seleccionado
  const [tipo, setTipo] = useState('creditos');
  const [enrolados, setEnrolados] = useState([]); // Lista de registros desde API
  const [rutSeleccionado, setRutSeleccionado] = useState('');
  const [nombreSeleccionado, setNombreSeleccionado] = useState('');

  // Manejo del tamaño de la ventana //
  const handleResize = () => setWindowWidth(window.innerWidth);

  // Cambio de titulo //
  useEffect(() => {
    setTitle(' Subir Carpeta Tributaria Enrolados');
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setTitle]);

  const availableWidth = isSidebarOpen ? windowWidth - 220 : windowWidth;

  // Validación del token //
  const isTokenValid = (token) => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp >= currentTime;
    } catch {
      return false;
    }
  };

  const handleTokenExpiration = useCallback(() => {
    Swal.fire({
      icon: 'error',
      title: 'Sesión expirada',
      text: 'El token ha expirado. Por favor, inicia sesión nuevamente.',
      footer: '▶️ Overwatch',
      showConfirmButton: false,
      background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
      color: '#f8fafc',
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        htmlContainer: 'swal-custom-text'
      },
      timer: 1500
    });
    sessionStorage.removeItem('authToken');
    setTimeout(() => {
      window.location.href = '/login';
    }, 1600);
  }, []);

  // Función para boton cancelar //
  const handleCancel = () => {
    setFile(null);
    if (inputRef.current) {
      inputRef.current.value = ""; // 👉 resetea el input file
    }
  };

  // cargar enrolados asociados al cliente //
  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    if (!token || !isTokenValid(token)) {
      handleTokenExpiration();
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const rutToken = decoded.usuario;

      setRut(rutToken);
      setNomUsuario(decoded.nombre);

      // 🚀 Llamado a fetchEnrolados directamente aquí //
      (async () => {
        try {
          const response = await axios.post(`${apiUrl}/enrolados/getenrolados`,
            { rut: rutToken },
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 10000,
            }
          );

          if (response.data && Array.isArray(response.data.rows)) {
            setEnrolados(response.data.rows);
          } else {
            setEnrolados([]);
          }
        } catch (error) {
          console.error("Error al cargar enrolados:", error);
        }
      })();

    } catch (error) {
      console.error("Error decodificando token:", error);
      handleTokenExpiration();
    }
  }, [apiUrl, handleTokenExpiration]);



  // Manejo del cambio de carpetas //
  const handleTipoChange = (event) => {
    setTipo(event.target.value);
  };

  // cargar todos los registros de la tabla bitacora //
  const fetchBitacora = useCallback(async () => {
    try {
      // Obtener el token desde sessionStorage //
      const token = sessionStorage.getItem('authToken');

      if (!token || !isTokenValid(token)) {
        handleTokenExpiration(); // Manejar la expiración del token y redirigir
        return; // Evitar continuar con el código si el token es inválido o expirado
      }

      setLoading(true);

      const response = await axios.post(`${apiUrl}/subircarpeta/getbitacora`,
        { RutCli: rut,     // rut del cliente
          RutEnr: '',      // rut del enrolado
          TipCli: 2        // 1=cliente 2=enrolado
         },
        {
          headers: {
            Authorization: `Bearer ${token}`,  // Incluir el token en el encabezado Authorization
          },
        }
      );

      if (response.data.success && response.data.rows) {
        setBitacora(response.data.rows); // Asignar los datos al estado //

      } else {
        setBitacora([]); // Limpiar datos si no hay resultados //
        Swal.fire({
          position: "center",
          icon: "info",
          title: "No hay datos para mostrar...",
          footer: '▶️ Overwatch',
          showConfirmButton: false,
          background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
          color: '#f8fafc',
          customClass: {
            popup: 'swal-custom-popup',
            title: 'swal-custom-title',
            htmlContainer: 'swal-custom-text'
          },
          timer: 1500
        });
      }
      
    } catch (error) {
      setBitacora([]); // También limpiar en caso de error de red
      Swal.fire({
        position: "center",
        icon: "error",
        title: "Error al obtener datos",
        text: error.message || "Intenta nuevamente.",
        showConfirmButton: false,
        background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
        color: '#f8fafc',
        customClass: {
          popup: 'swal-custom-popup',
          title: 'swal-custom-title',
          htmlContainer: 'swal-custom-text'
        },
        timer: 1500
      });
    } finally {
      setLoading(false);
    }
  }, [setBitacora, apiUrl, rut, handleTokenExpiration]);

  // llamar a cargar bitacora //
  useEffect(() => {
    if (rut) {
      fetchBitacora();
    }
  }, [rut, fetchBitacora]);


  // Configuración de MUI DataGrid //
  const columns = [
    {
      field: 'id',
      headerName: 'Id de la Cesión',
      width: 120,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Id de la Cesión" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Id
          </span>
        </Tooltip>
      ),
    },
    {
      field: "fecha",
      headerName: "Fecha Upload",
      width: 160,
      sortable: true,
      headerAlign: "center",
      align: "center",

      renderHeader: () => (
        <Box
          sx={{
            textAlign: "center",
            fontSize: "0.8rem",
            backgroundColor: "#85c1e9",
            color: "black",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Fecha Upload
        </Box>
      ),

      renderCell: (params) => {
        const valor = params.value;

        if (!valor) return "";

        const fecha = new Date(valor);

        const fechaFormateada = fecha.toLocaleString("es-CL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });

        return (
          <Box
            sx={{
              fontSize: "0.7rem",
              textAlign: "center",
              width: "100%",
            }}
          >
            {fechaFormateada}
          </Box>
        );
      },
    },
    {
      field: 'tipo',
      headerName: "Tipo",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Tipo" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Tipo
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'rutcli',
      headerName: "Rut Cliente",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Rut del Cliente" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Rut Cliente
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'rutenr',
      headerName: "Rut Enrolado",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Rut del Enrolado" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Rut Enrolado
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'nombre',
      headerName: "Razón Social",
      width: 785,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Razón Social" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Razón Social
          </span>
        </Tooltip>
      ),
    },
  ];

  /** Subir PDF de carpeta tributaria **/
  const handleUpload = async () => {
    if (!file) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo requerido',
        text: 'Debe selecciona un archivo PDF.',
        footer: '▶️ Overwatch',
        background: 'rgba(30, 41, 59, 0.9)',
        color: '#f8fafc'
      });
      return;
    }

    try {
      const token = sessionStorage.getItem('authToken');
      if (!token || !isTokenValid(token)) {
        handleTokenExpiration();
        return;
      }

      const tipoCarpeta = tipo === "creditos" ? 1 : 2;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("RutCli", rut);
      formData.append("RutEnr", rutSeleccionado);
      formData.append("NomCli", nombreSeleccionado);
      formData.append("TipCli", tipoCarpeta); // 1=créditos 2=personalizada //

      const response = await axios.post(`${apiUrl}/subircarpeta/uploadcarpeta`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      if (response.data.success) {
         Swal.fire({
          icon: 'success',
          title: 'Archivo subido',
          text: response.data.message || 'La carpeta tributaria fue subida correctamente.',
          footer: '▶️ Overwatch',
          background: 'rgba(30, 41, 59, 0.9)',
          color: '#f8fafc'
        });
        setFile(null);
        if (inputRef.current) {
          inputRef.current.value = ""; // 👉 limpia input después de subir
        }
        fetchBitacora(); // refrescar tabla

      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response.data.message || 'No se pudo subir el archivo.',
          footer: '▶️ Overwatch',
          background: 'rgba(30, 41, 59, 0.9)',
          color: '#f8fafc'
        });
      }

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error de conexión',
        text: error.message || 'Intenta nuevamente.',
        footer: '▶️ Overwatch',
        background: 'rgba(30, 41, 59, 0.9)',
        color: '#f8fafc'
      });
    }
  };

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          mb: 1,
          mt: 2,
          height: '70px',
          flexWrap: 'wrap', // Permite que los elementos se acomoden en múltiples líneas si es necesario
          maxWidth: '100%',
        }}
      >
        <TextField
          label="Rut"
          value={rut}
          onChange={(e) => setRut(e.target.value)}
          variant="outlined"
          fullWidth
          required
          autoComplete="off" // Desactivar autocompletado //
          disabled
          sx={{
            width: '120px',
            height: '40px',
            '& .MuiOutlinedInput-root': {
              height: '40px',
              '& input': {
                color: '#ca531f',
                WebkitTextFillColor: '#ca531f',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#ca531f',
            },
          }}
        />

        {/* Campo de Nombre de Usuario */}
        <TextField
          id="nom-usuario-input"
          label="Cliente"
          value={nomUsuario}
          variant="outlined"
          disabled
          sx={{
            width: '480px',
            height: '40px',
            '& .MuiOutlinedInput-root': {
              height: '40px',
              '& input': {
                color: '#ca531f',
                WebkitTextFillColor: '#ca531f',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#ca531f',
            },
          }}
        />

        {/* Selector de enrolados */}
        <TextField
          select
          id="enrolado-selector"
          label="Enrolado"
          value={rutSeleccionado}
          onChange={(e) => {
            const rut = e.target.value;
            setRutSeleccionado(rut);
            // Buscar el enrolado correspondiente por RUT
            const enrolado = enrolados.find((item) => item.rut_enr === rut);
            
            // Si lo encuentra, guardar el nombre
            if (enrolado) {
              setNombreSeleccionado(enrolado.nombre_enr || '');
            } else {
              setNombreSeleccionado('');
            }
          }}
          variant="outlined"
          sx={{
            width: '400px',
            height: '40px',
            '& .MuiOutlinedInput-root': { height: '40px', '& input': { color: '#ca531f', WebkitTextFillColor: '#ca531f' } },
            '& .MuiInputLabel-root': { color: '#ca531f' },
          }}
        >
          {enrolados.length > 0 ? (
            enrolados.map((item) => (
              <MenuItem key={item.rut_enr} value={item.rut_enr}>
                {item.nombre_enr || item.rut_enr}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No hay registros</MenuItem>
          )}
        </TextField>

        <TextField
          id="tipo-input"
          select
          label="Tipo"
          value={tipo}
          onChange={handleTipoChange}
          variant="outlined"
          required
          sx={{
            width: 150,
            height: '40px',
            '& .MuiOutlinedInput-root': { height: '40px' },
            '& .MuiSelect-select': {
              textAlign: 'center',
            },
          }}
        >
          <MenuItem value="creditos">Créditos</MenuItem>
          <MenuItem value="personalizada">Personalizada</MenuItem>
        </TextField>

        {/* mostrar nombre del archivo */}
        <TextField
          value={file ? file.name : ""}
          label="Archivo seleccionado..."
          variant="outlined"
          disabled
          sx={{
            mt: 0,
            width: '815px',
            height: '40px',
            '& .MuiOutlinedInput-root': {
              height: '40px',
              '& input': {
                color: '#ca531f',
                WebkitTextFillColor: '#ca531f',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#ca531f',
            },
          }}
        />

         {/* Subir PDF */}
        <input
          accept="application/pdf"
          style={{ display: 'none' }}
          id="upload-pdf"
          type="file"
          ref={inputRef} // 👉 referencia al input
          disabled={!rutSeleccionado}
          onChange={(e) => setFile(e.target.files[0] || null)}
        />
        <label htmlFor="upload-pdf">
          <Button
            variant="contained"
            component="span"
            sx={{
              width: '145px',
              height: '40px',
              textTransform: 'none',
              backgroundColor: '#4caf50',
              color: 'white',
              '&:hover': { backgroundColor: '#388e3c', color: 'white' },
            }}>
            <RiFolderCheckFill style={{ fontSize: '1.5rem', marginRight: '8px' }} />
            Buscar PDF
          </Button>
        </label>

        {/* Botón subir */}
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file}
          sx={{
            width: '145px',
            height: '40px',
            textTransform: 'none',
            backgroundColor: '#8b888e',
            color: 'white',
            '&:hover': { backgroundColor: '#9759c7', color: 'white' },
          }}>
          <RiUpload2Line style={{ fontSize: '1.5rem', marginRight: '8px' }} />
          Subir PDF
        </Button>

        {/* Botón Cancelar */}
        <Tooltip title="Cancelar Todo" placement="top" arrow>
          <Button
            variant="contained"
            onClick={handleCancel}
            sx={{
              width: '145px',
              height: '40px',
              textTransform: 'none',
              backgroundColor: '#8b888e', // Color de fondo para cancelar
              color: 'white',
              '&:hover': {
                backgroundColor: '#b9770e', // Color al pasar el cursor //
                color: 'white',
              },
              '& .MuiButton-startIcon': {
                color: 'white',
              },
            }}
          >
            {/* Icono de cancelar */}
            <RiReplyAllFill className="m-2" style={{ fontSize: '1.5rem' }} />
            Cancelar
          </Button>
        </Tooltip>
      </Box>

      {/* Configuración MIU DataGrid */}
      <Box
        sx={{
          display: 'block',
          boxSizing: 'border-box',
          p: '1px',
          mt: 8,
          maxWidth: '98%',
          width: availableWidth, // Ajuste dinámico según sidebar
          overflowX: 'auto',
        }}
      >
        <Box sx={{ mt: 1 }}>
          <ThemeProvider theme={customTheme}>
            <DataGrid
              rows={bitacora}
              columns={columns}
              getRowId={(row) => row.id}
              loading={loading}
              density="compact"
              rowHeight={38}
              headerHeight={40}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
                density: 'compact',
              }}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              slots={{
                toolbar: CustomToolbar,
              }}
              // slots={{
              //   toolbar: GridToolbar,
              // }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 400 },
                },
              }}
              sx={{
                border: 'none',
                backgroundColor: modo === 'dark' ? '#2b2b2b' : '#eaeded',

                // 🎨 CABECERA
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: modo === 'dark' ? '#85c1e9 !important' : '#85c1e9 !important',
                },
                '& .MuiDataGrid-columnHeadersInner': {
                  backgroundColor: modo === 'dark' ? '#85c1e9 !important' : '#85c1e9 !important',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: modo === 'dark' ? '#85c1e9 !important' : '#85c1e9 !important',
                  color: modo === 'dark' ? '#000 !important' : '#000 !important',
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  textAlign: 'center',
                  borderBottom: '2px solid #a9cce3',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  textAlign: 'center',
                  width: '100%',
                },

                // 🔹 permite respetar la alineación por columna
                '& .MuiDataGrid-cell--textLeft': {
                  justifyContent: 'flex-start !important',
                  textAlign: 'left !important',
                },
                '& .MuiDataGrid-cell--textCenter': {
                  justifyContent: 'center !important',
                  textAlign: 'center !important',
                },
                '& .MuiDataGrid-cell--textRight': {
                  justifyContent: 'flex-end !important',
                  textAlign: 'right !important',
                },

                // 🧾 CELDAS
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  lineHeight: 1,
                  textAlign: 'center',
                  p: '2px 8px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  color: modo === 'dark' ? '#B1AAAE' : '#000',
                },

                // 🩶 FILAS
                '& .MuiDataGrid-row': {
                  backgroundColor: modo === 'dark' ? '#222222' : '#f2f3f4',
                  '&:hover': {
                    backgroundColor: modo === 'dark' ? '#3e3633' : '#eaf2f8',
                  },
                },

                // 🧭 FOOTER '#B1AAAE'
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #ccc',
                  minHeight: '30px',
                  backgroundColor: modo === 'dark' ? '#3e3633' : '#f1f3f4',
                  color: modo === 'dark' ? '#3e3633' : '#000',
                },

                // 🔹 SCROLL
                '& .MuiDataGrid-virtualScroller': {
                  maxHeight: '600px',
                },

                // Color de selección y le quita la caja de seleción
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none !important',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none !important',
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: modo === 'dark' ? '#3e3633' : '#B1AAAE !important', // color de fila seleccionada
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: modo === 'dark' ? '#3e3633' : '#B1AAAE !important', // hover opcional
                },
              }}
            />
          </ThemeProvider>
        </Box>
      </Box>
    </div>
  );
};

export default Subircarpetaenr;
